import React from 'react';
import {useIdleTimer} from 'react-idle-timer';
import {useTranslation} from 'react-i18next';
import {useIsAuthenticated, useMsal} from '@azure/msal-react';
import {useSnackbar} from 'notistack';
import {useLocation, useNavigate} from 'react-router-dom';
import {jwtDecode} from 'jwt-decode';

import {environment} from '@shared/config/environment';
import {
  signOutUser,
  adfsLogOutService
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';

import AuthUtils from 'src/utils/AuthUtils';
import {useAuthContext} from '@context/AuthContext';
import {APP} from 'src/styles/variables';
import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';
import FormConstants from '@shared/constants/FormConstants';
import EncryptUtils from 'src/utils/EncryptUtils';
import {BASE_ROUTE} from 'src/routes/BaseRoute';
import {PUBLIC_ROUTES} from 'src/routes/RouteConstants';

import SnackBarConfig from '@components/snackbar/SnackbarConfig';

const AutoLogout = (props: any): React.ReactElement => {
  const {t} = useTranslation();
  const {logout} = useAuthContext();
  const isAzureAuthenticated = useIsAuthenticated();
  const {instance} = useMsal();
  const {enqueueSnackbar} = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();

  const timeout = 1000 * 60 * environment.InActivityTimeOutInMins;
  const promptBeforeIdle =
    1000 * 60 * environment.InActivityPromptBeforeIdleInMins;
  const {children} = props;

  const [remaining, setRemaining] = React.useState<number>(timeout);
  const [open, setOpen] = React.useState<boolean>(false);

  let userData: any;

  if (AuthUtils.getAuthToken()) {
    userData = jwtDecode(AuthUtils.getAuthToken() || '');
  }

  const handleLogOut = async () => {
    try {
      const data: any = {
        sql_data: {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.LOGOUT_SUCCESS
        }
      };

      if (userData?.user?.type === FormConstants.LOGIN_TYPE.ADFS) {
        await adfsLogOutService({origin: window.location.origin});
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        return;
      }

      const encryptRequestData = EncryptUtils.encryptedData(data);
      await signOutUser({
        tk: encryptRequestData
      });

      if (isAzureAuthenticated || AuthUtils.getIsAzureAuthenticated()) {
        AuthUtils.clearStorage();
        navigate(`/${BASE_ROUTE}/` + PUBLIC_ROUTES.LOGIN);
        instance.logoutRedirect({
          postLogoutRedirectUri: '/'
        });
        return;
      } else {
        logout();
      }
      AuthUtils.clearStorage();

      const prevRoute = `${location?.pathname}${location?.search}`;
      AuthUtils.setPrevRoute(prevRoute);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const onIdle = () => {
    setOpen(false);
    handleLogOut();
  };

  const onActive = () => {
    setOpen(false);
  };

  const onPrompt = () => {
    setOpen(true);
  };

  const {getRemainingTime, activate} = useIdleTimer({
    onIdle,
    onActive,
    onPrompt,
    timeout,
    promptBeforeIdle,
    throttle: 500
  });

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(Math.ceil(getRemainingTime() / 1000));
    }, 500);

    return () => {
      clearInterval(interval);
    };
  });

  const renderDialog = (dialogProps: any): React.ReactElement => {
    return <ActionModalDialog {...dialogProps} />;
  };

  return (
    <>
      {children}

      {open &&
        renderDialog({
          open: open,
          remainingSec: remaining,
          yesLabelText: t('PRIVATE.AUTO_LOGOUT.STAY_LOGGED_IN', {
            ns: 'private'
          }),
          noLabelText: t('PRIVATE.AUTO_LOGOUT.LOG_OUT', {ns: 'private'}),
          yesButtonColor: APP.PALETTE.ROLES.GREEN,
          noButtonColor: APP.PALETTE.ROLES.RED,
          AutoLogoutScreen: true,
          handleAgreeBtnClick: activate,
          handleCloseBtnClick: handleLogOut
        })}
    </>
  );
};

export default AutoLogout;

